import { ReactNode } from 'react';

import { Stack, Divider, StackProps, DividerProps } from '@mui/material';
import clsx from 'clsx';

import Button, { ButtonProps as ButtonPropsBase } from '../Button';
import { tokens } from '../../../theme/generated/tokens';
import { SxProps, WithRef } from '../../../types';
import { mergeSx } from '../../../utils';

type ButtonProps = Omit<ButtonPropsBase, 'size'>;
type SplitButtonVariant = Extract<
  ButtonPropsBase['variant'],
  'primary' | 'secondary'
>;

export type SplitButtonProps = {
  size?: ButtonPropsBase['size'];
  disabled?: boolean;
  variant?: SplitButtonVariant;
  slots?: {
    rootProps?: StackProps;
    dividerProps?: DividerProps;
    leftButtonProps?: ButtonProps;
    leftButton?: ReactNode;
    rightButtonProps?: ButtonProps;
    rightButton?: ReactNode;
  };
  className?: string;
  sx?: SxProps;
};

export const splitButtonClasses = {
  root: 'SplitButton-root',
  divider: 'SplitButton-divider',
  leftButton: 'SplitButton-leftButton',
  rightButton: 'SplitButton-rightButton',
};

export const SplitButton = ({
  ref,
  size = 'md',
  disabled,
  variant = 'primary',
  slots: {
    rootProps,
    dividerProps,
    leftButtonProps,
    leftButton,
    rightButtonProps,
    rightButton,
  } = {},
}: WithRef<HTMLDivElement, SplitButtonProps>) => {
  return (
    <Stack
      direction="row"
      {...rootProps}
      className={clsx(splitButtonClasses.root, rootProps?.className)}
      ref={ref}
    >
      <Button
        size={size}
        disabled={disabled}
        variant={variant}
        {...leftButtonProps}
        className={clsx(
          splitButtonClasses.leftButton,
          leftButtonProps?.className
        )}
        sx={mergeSx(
          { borderTopRightRadius: 0, borderBottomRightRadius: 0 },
          leftButtonProps?.sx
        )}
      >
        {leftButton}
      </Button>
      <Divider
        orientation="vertical"
        flexItem
        {...dividerProps}
        className={clsx(splitButtonClasses.divider, dividerProps?.className)}
        sx={mergeSx(
          (() => {
            if (disabled) {
              return { borderColor: tokens.theme.disabled.fg };
            }
            if (variant === 'primary') {
              return {
                borderColor: tokens.theme.fg.action.primary,
                opacity: 0.5,
              };
            }
            if (variant === 'secondary') {
              return {
                borderColor: tokens.theme.fg.action.onSecondary,
                opacity: 0.3,
              };
            }
            return undefined;
          })(),
          dividerProps?.sx
        )}
      />
      <Button
        size={size}
        disabled={disabled}
        variant={variant}
        {...rightButtonProps}
        sx={mergeSx(
          { borderTopLeftRadius: 0, borderBottomLeftRadius: 0 },
          rightButtonProps?.sx
        )}
      >
        {rightButton}
      </Button>
    </Stack>
  );
};

SplitButton.displayName = 'SplitButton';
